import { useState, useCallback } from "react";
import { MAX_SEGMENTS } from "../common/constants";
import { type Segment } from "../types/types";
import { type ValidationError, validateSegments } from "../utils/validate-segments";
import { useHeadroomContext } from "../context/HeadRoomProvider";

interface ColumnRange {
  isNumeric: boolean;
  min?: number;
  max?: number;
}

interface UseSegmentProps {
  index: number;
  initialSegments: Segment[];
  initialNumberOfSegments: number;
  columnRanges: Record<string, ColumnRange>;
  type: string;
  onSegmentChange?: () => void;
}

interface SegmentManagement {
  segments: Segment[];
  numberOfSegments: number;
  // errors: ValidationError[];
  handleMinValueChange: (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleMaxValueChange: (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddSegment: () => void;
  handleRemoveSegment: (id: number) => () => void;
  handleRemoveLastSegment: () => void;
}

export const useSegments = ({
  index,
  initialSegments,
  initialNumberOfSegments,
  columnRanges,
  type,
  onSegmentChange,
}: UseSegmentProps): SegmentManagement => {
  const { updateSegmentGroupSegments } = useHeadroomContext();
  const column = type && columnRanges[type];
  const isNumeric = column?.isNumeric;
  const min = column?.min;
  const max = column?.max;

  const generateSegmentId = useCallback(() => {
    return Math.floor(Math.random() * 1000000000);
  }, []);

  const computedInitialSegments = (() => {
    if (
      initialSegments.length >= 2 &&
      isNumeric &&
      typeof min === "number" &&
      typeof max === "number" &&
      initialSegments.every((segment) => segment.minValue >= min && segment.maxValue <= max)
    ) {
      return initialSegments.map((segment) => ({
        ...segment,
        id: generateSegmentId(),
        minValue: Number(segment.minValue.toFixed(2)),
        maxValue: Number(segment.maxValue.toFixed(2)),
      }));
    }

    if (isNumeric && typeof min === "number" && typeof max === "number") {
      const average = Number(((min + max) / 2).toFixed(2));

      return [
        { id: 0, minValue: Number(min.toFixed(2)), maxValue: average },
        { id: 1, minValue: average, maxValue: Number(max.toFixed(2)) },
      ];
    }

    return [
      { id: 0, minValue: 0, maxValue: 0 },
      { id: 1, minValue: 0, maxValue: 0 },
    ];
  })();

  const [segments, setSegments] = useState<Segment[]>(computedInitialSegments);
  const [numberOfSegments, setNumberOfSegments] = useState(computedInitialSegments.length || initialNumberOfSegments);
  // const [errors, setErrors] = useState<ValidationError[]>(validateSegments(computedInitialSegments));

  const updateSegmentsInContext = useCallback(
    (newSegments: Segment[]) => {
      const roundedSegments = newSegments.map((segment) => ({
        ...segment,
        id: segment.id || generateSegmentId(),
        minValue: Number(segment.minValue.toFixed(2)),
        maxValue: Number(segment.maxValue.toFixed(2)),
      }));

      setSegments(roundedSegments);
      setNumberOfSegments(roundedSegments.length);
      // setErrors(validateSegments(roundedSegments));
      updateSegmentGroupSegments(index, roundedSegments);
      onSegmentChange?.();
    },
    [updateSegmentGroupSegments, index, onSegmentChange, generateSegmentId]
  );

  const handleMinValueChange = useCallback(
    (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      if (!isNaN(value)) {
        const newSegments = segments.map((segment) =>
          segment.id === id ? { ...segment, minValue: Number(value.toFixed(2)) } : segment
        );
        updateSegmentsInContext(newSegments);
      }
    },
    [segments, updateSegmentsInContext]
  );

  const handleMaxValueChange = useCallback(
    (id: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = Number(event.target.value);
      if (!isNaN(value)) {
        const newSegments = segments.map((segment) =>
          segment.id === id ? { ...segment, maxValue: Number(value.toFixed(2)) } : segment
        );
        updateSegmentsInContext(newSegments);
      }
    },
    [segments, updateSegmentsInContext]
  );

  const handleAddSegment = useCallback(() => {
    if (numberOfSegments < MAX_SEGMENTS && isNumeric && typeof min === "number" && typeof max === "number") {
      const newNumberOfSegments = numberOfSegments + 1;
      const segmentWidth = (max - min) / newNumberOfSegments;
      const newSegments = Array.from({ length: newNumberOfSegments }, (_, i) => ({
        id: generateSegmentId(),
        minValue: Number((min + i * segmentWidth).toFixed(2)),
        maxValue:
          i === newNumberOfSegments - 1 ? Number(max.toFixed(2)) : Number((min + (i + 1) * segmentWidth).toFixed(2)),
      }));
      updateSegmentsInContext(newSegments);
    }
  }, [numberOfSegments, isNumeric, min, max, updateSegmentsInContext, generateSegmentId]);

  const handleRemoveSegment = useCallback(
    (id: number) => () => {
      if (segments.length > 2) {
        const newSegments = segments.filter((segment) => segment.id !== id);
        if (isNumeric && typeof min === "number" && typeof max === "number") {
          const segmentWidth = (max - min) / newSegments.length;
          const rebalancedSegments = newSegments.map((segment, i) => ({
            ...segment,
            id: i,
            minValue: Number((min + i * segmentWidth).toFixed(2)),
            maxValue:
              i === newSegments.length - 1 ? Number(max.toFixed(2)) : Number((min + (i + 1) * segmentWidth).toFixed(2)),
          }));
          updateSegmentsInContext(rebalancedSegments);
        } else {
          updateSegmentsInContext(newSegments);
        }
      }
    },
    [segments, isNumeric, min, max, updateSegmentsInContext]
  );

  const handleRemoveLastSegment = useCallback(() => {
    if (numberOfSegments > 2) {
      const newSegments = segments.slice(0, -1);
      if (isNumeric && typeof min === "number" && typeof max === "number") {
        const segmentWidth = (max - min) / newSegments.length;
        const rebalancedSegments = newSegments.map((segment, i) => ({
          ...segment,
          id: i,
          minValue: Number((min + i * segmentWidth).toFixed(2)),
          maxValue:
            i === newSegments.length - 1 ? Number(max.toFixed(2)) : Number((min + (i + 1) * segmentWidth).toFixed(2)),
        }));
        updateSegmentsInContext(rebalancedSegments);
      } else {
        updateSegmentsInContext(newSegments);
      }
    }
  }, [segments, numberOfSegments, isNumeric, min, max, updateSegmentsInContext]);

  return {
    segments,
    numberOfSegments,
    // errors,
    handleMinValueChange,
    handleMaxValueChange,
    handleAddSegment,
    handleRemoveSegment,
    handleRemoveLastSegment,
  };
};
